import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { getCurrentLocation } from '../../services/location';

/**
 * DEV-ONLY location diagnostic. CONTROLLED as of 2026-08-20 — the header's LOC
 * pill owns `open`, so this file no longer carries a collapsed state, a
 * persisted open/closed key, or the `?locdebug` URL condition that had made it
 * effectively unreachable ("the LOC tool which we hid").
 *
 * Rendered only on non-prod deploys (isBranchDeploy() at the call site, and
 * again in DevToolsPills so the opener cannot outlive the panel), so it never
 * appears for real testers. A branch deploy is a separate origin, so its PWA starts with fresh
 * location permission — a pristine cold-start for reproducing the bug below.
 *
 * "Reset location state" clears the four localStorage keys that keep an existing
 * user out of the cold path; "Test GPS" then calls the real getCurrentLocation()
 * so we can see whether iOS shows the native geolocation dialog in standalone:
 *
 *   ✅ OK tier=high-accuracy  → permission worked, GPS fix
 *   ✅ OK tier=cache          → live attempt failed but GPS cache saved it (reset + retry)
 *   ❌ FAIL code=1            → permission DENIED
 *   ❌ FAIL code=3            → timeout / native prompt never appeared (standalone bug)
 */

const KEYS = {
  permission: 'location_permission', // our app flag ('granted'/'denied')
  sharing: 'location_sharing_enabled', // sharing toggle
  gpsCache: 'last_known_gps', // cached GPS fix (masks live failures)
  lastUpdated: 'location_last_updated', // 5-min guard timestamp
};

const GUARD_MS = 5 * 60 * 1000;

const fmtAge = (ts) => {
  if (!ts) return '—';
  const mins = Math.round((Date.now() - Number(ts)) / 60000);
  return `${mins} min ago`;
};

const row = { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' };
const label = { color: '#9AA0AA', flexShrink: 0 };
const val = { color: '#fff', fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-all' };

const LocationDebugPanel = ({ open = false, onClose }) => {
  const [snapshot, setSnapshot] = useState({});
  const [osPermission, setOsPermission] = useState('checking…');
  const [result, setResult] = useState('(not run)');
  const [testing, setTesting] = useState(false);

  const readState = useCallback(async () => {
    let gps = null;
    try {
      const raw = localStorage.getItem(KEYS.gpsCache);
      gps = raw ? JSON.parse(raw) : null;
    } catch (e) {
      /* ignore */
    }
    const lastUpdated = localStorage.getItem(KEYS.lastUpdated);
    setSnapshot({
      permission: localStorage.getItem(KEYS.permission) || '(unset)',
      sharing: localStorage.getItem(KEYS.sharing) || '(unset)',
      gps: gps
        ? `${gps.latitude?.toFixed(4)},${gps.longitude?.toFixed(4)} · ${fmtAge(gps.timestamp)}`
        : '(none)',
      guard:
        lastUpdated && Date.now() - Number(lastUpdated) < GUARD_MS
          ? `ACTIVE · ${fmtAge(lastUpdated)}`
          : 'inactive',
    });

    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        setOsPermission(status.state);
      } else {
        setOsPermission('Permissions API unsupported');
      }
    } catch (e) {
      setOsPermission(`unsupported (${e.message})`);
    }
  }, []);

  useEffect(() => {
    readState();
  }, [readState]);

  const handleReset = () => {
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
    setResult('(reset — 4 keys cleared, now in cold-start state)');
    readState();
  };

  const handleTest = async () => {
    setTesting(true);
    setResult('requesting GPS… watch for the iOS "Allow location" dialog');
    const t0 = Date.now();
    try {
      const c = await getCurrentLocation();
      setResult(
        `✅ OK · tier=${c._gpsTier} · acc=${c._gpsAccuracy ?? '?'}m · ${Date.now() - t0}ms\n${c.latitude.toFixed(5)}, ${c.longitude.toFixed(5)}`
      );
    } catch (e) {
      setResult(
        `❌ FAIL · code=${e.code} · ${Date.now() - t0}ms\n${e.message}\n(1=denied · 2=unavailable · 3=timeout/no-prompt)`
      );
    } finally {
      setTesting(false);
      readState();
    }
  };

  // Controlled: the People header's LOC pill owns `open`. The old ?locdebug=1
  // summoning trick and the collapsed pill are both gone (2026-08-20).
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(8px + env(safe-area-inset-top))',
        left: '8px',
        right: '8px',
        zIndex: 3000,
        background: 'rgba(17,17,19,0.97)',
        border: '1px solid #333',
        borderRadius: '14px',
        padding: '12px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>
          🛠 Location debug (throwaway)
        </span>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#9AA0AA', fontSize: '16px' }}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={row}>
          <span style={label}>our permission flag</span>
          <span style={val}>{snapshot.permission}</span>
        </div>
        <div style={row}>
          <span style={label}>sharing flag</span>
          <span style={val}>{snapshot.sharing}</span>
        </div>
        <div style={row}>
          <span style={label}>OS permission</span>
          <span style={val}>{osPermission}</span>
        </div>
        <div style={row}>
          <span style={label}>GPS cache</span>
          <span style={val}>{snapshot.gps}</span>
        </div>
        <div style={row}>
          <span style={label}>5-min guard</span>
          <span style={val}>{snapshot.guard}</span>
        </div>
      </div>

      <div
        style={{
          background: '#000',
          borderRadius: '8px',
          padding: '8px 10px',
          color: '#fff',
          fontFamily: 'monospace',
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          minHeight: '40px',
        }}
      >
        {result}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <ButtonRounded
          text="Reset location state"
          onClick={handleReset}
          size="full"
          variant="secondary"
        />
        <ButtonRounded
          text={testing ? 'Testing…' : 'Test GPS'}
          onClick={handleTest}
          size="full"
          variant="primary"
          disabled={testing}
        />
      </div>
    </div>
  );
};

LocationDebugPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default LocationDebugPanel;
